import React, { useEffect, useId, useState } from "react";
import WrapperInput, { IWrapperInput } from "./WrapperInput";
import {
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { cloneDeep, isArray, isString } from "lodash";
import { v4 } from "uuid";
import { LuChevronDown, LuXCircle } from "react-icons/lu";
import { TagModeType } from "../../../types/filter.type";

type OptionType = { label: string; value: TagModeType; color: string };
type TagType = { value: string; mode: TagModeType; color: string };

export interface ITextTagInput2 extends Omit<IWrapperInput, "children"> {
  placeholder?: string;
  errorMessage?: string | undefined;
  disabled?: boolean;
  readOnly?: boolean;
  endAdornment?: React.ReactNode;
  textFieldProps?: TextFieldProps;
  pending?: boolean;
  defaultValue?: string[];
  toggleClear?: number;
  onChange?: (val: TagType[] | string) => void;
}

const options: OptionType[] = [
  { label: "Add with and", value: "and", color: "primary.main" },
  { label: "Add with or", value: "or", color: "secondary.main" },
  { label: "Add with not", value: "not", color: "error.main" },
  { label: "Equal", value: "equal", color: "info.main" },
];

const heights = {
  input: "42px",
};

function TextTagInput2({
  id,
  label,
  labelWidth = "20%",
  defaultValue,
  placeholder,
  required,
  errorMessage,
  disabled,
  readOnly,
  endAdornment,
  pending,
  textFieldProps,
  toggleClear,
  onChange,
  ...wrapperProps
}: ITextTagInput2) {
  const idLabel = useId();
  const [isTag, setIsTag] = useState<boolean>(true);
  const [text, setText] = useState<string>("");
  const [tags, setTags] = useState<TagType[]>([]);
  const [anchorMenu, setAnchorMenu] = useState<HTMLElement | null>(null);
  const [option, setOption] = useState<OptionType>(options[0]);

  const handleSaveTag = () => {
    if (!text || pending) return;
    if (isTag) {
      const newTags = [
        ...(tags || []),
        { value: text, mode: option.value, color: option.color } as TagType,
      ];
      setTags?.(newTags);
      onChange?.(newTags);
      setText("");
    } else {
      onChange?.(text);
    }
  };

  const handleModeChange = (option: OptionType) => {
    setOption(option);
    setAnchorMenu(null);
    if (option.value === "equal") {
      setTags([]);
      setIsTag(false);
    } else {
      setText("");
      setIsTag(true);
    }
  };

  const handleDeleteTag = (index: number) => {
    if (pending) return;
    const tagsClone = cloneDeep(tags || []);
    tagsClone?.splice(index, 1);
    setTags?.(tagsClone);
    onChange?.(tagsClone);
  };

  const handleClear = () => {
    setText("");
    setTags([]);
    onChange?.([]);
  };

  useEffect(() => {
    setTags([]);
    setText("");
  }, [toggleClear]);

  return (
    <>
      <Menu
        anchorEl={anchorMenu}
        open={!!anchorMenu}
        onClose={() => setAnchorMenu(null)}
        slotProps={{ paper: { elevation: 1 } }}
      >
        {options.map((option) => {
          return (
            <MenuItem
              onClick={() => {
                handleModeChange(option);
              }}
              key={option.value}
              sx={{ color: option.color }}
            >
              {option.label}
            </MenuItem>
          );
        })}
      </Menu>
      <WrapperInput
        id={idLabel || id}
        label={label}
        required={required}
        labelWidth={labelWidth}
        {...wrapperProps}
      >
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Stack
            gap={0.5}
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            sx={{
              flex: 1,
              width: "100%",
              border: "2px solid",
              borderColor: "divider",
              borderRadius: "10px",
              px: 1,
              py: 0.5,
              "&:focus-within": {
                borderColor: "primary.main",
              },
            }}
          >
            {isArray(tags) &&
              tags?.length > 0 &&
              tags.map((tag, index) => {
                return (
                  <Chip
                    key={v4()}
                    label={tag.value}
                    onDelete={() => handleDeleteTag(index)}
                    sx={{ "& .MuiChip-label": { color: tag.color } }}
                  />
                );
              })}
            <TextField
              id={idLabel || id}
              variant="outlined"
              fullWidth
              value={text}
              onChange={(e) => setText(e.target.value as string)}
              placeholder={placeholder}
              autoComplete="off"
              spellCheck={false}
              helperText={errorMessage}
              slotProps={{
                input: {
                  readOnly,
                  disabled,
                  endAdornment: (
                    <>
                      {((isArray(tags) && tags?.length > 0) ||
                        (!isTag && isString(text) && text?.length > 0)) && (
                        <IconButton onClick={handleClear}>
                          <LuXCircle size={16} />
                        </IconButton>
                      )}
                    </>
                  ),
                },
              }}
              onKeyUp={(e) => {
                if (e.which === 13) {
                  handleSaveTag();
                }
              }}
              sx={{
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: "40%",
                "& .MuiFormHelperText-root": {
                  color: "error.main",
                  fontStyle: "italic",
                },
                "& .MuiInputBase-root": {
                  height: heights.input,
                  px: 0,
                  py: 0,
                  "& .MuiInputBase-input": {
                    height: heights.input,
                    px: 0,
                    py: 1,
                  },
                  "& fieldset": { border: "none" },
                },
              }}
            />
          </Stack>
          <Button
            onClick={(e) => setAnchorMenu(e.currentTarget)}
            disabled={pending}
            variant="contained"
            endIcon={<LuChevronDown size={14} />}
            sx={{
              backgroundColor: option.color,
              color: "common.white",
              boxShadow: "none",
              px: 1,
              minWidth: "unset",
              width: "70px",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {option.value}
          </Button>
        </Stack>
      </WrapperInput>
    </>
  );
}

export default TextTagInput2;
